import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { fetchCheckpointStandards as fetchCheckpointStandardsService } from '@/domains/checkpoint/checkpointStandardsApi';
import type { StandardsData } from '@/domains/checkpoint/checkpoint.types';

interface HubStandardsState {
    standardsData: StandardsData;
    isLoading: boolean;
    error: Error | null;
    lastFetched: number | null;
}

const CACHE_TIME = 5 * 60 * 1000;

const useHubStandardsStore = create<
    HubStandardsState & {
        fetchStandards: (force?: boolean) => Promise<void>;
        clearCache: () => void;
    }
>()(
    persist(
        (set, get) => ({
            standardsData: {},
            isLoading: false,
            error: null,
            lastFetched: null,

            fetchStandards: async (force = false) => {
                const { lastFetched, standardsData } = get();
                const now = Date.now();
                const hasCachedData = Object.keys(standardsData ?? {}).length > 0;

                if (!force && lastFetched && now - lastFetched < CACHE_TIME && hasCachedData) {
                    return;
                }

                set({ isLoading: true, error: null });

                try {
                    const data = await fetchCheckpointStandardsService();
                    set({
                        standardsData: data,
                        isLoading: false,
                        lastFetched: now,
                    });
                } catch (error) {
                    set({ error: error as Error, isLoading: false });
                }
            },

            clearCache: () =>
                set({
                    standardsData: {},
                    lastFetched: null,
                    error: null,
                }),
        }),
        {
            name: 'hub-standards-storage',
            partialize: (state) => ({
                standardsData: state.standardsData,
                lastFetched: state.lastFetched,
            }),
        },
    ),
);

export default useHubStandardsStore;
